import { getExpertById } from '@api/experts';
import SkyBluePrevious from '@assets/images/icons/sky-blue-previous.svg';
import Img from '@components/Atoms/Img';
import { ExpertInterface } from '@interfaces/index';
import { Grid, Skeleton } from '@mui/material';
import { MotionText } from '@shared/atoms';
import { LoadingBackdrop } from '@shared/molecules';
import { sanitizeHtml } from '@utils/string.util';
import { castResponse } from 'custom.d';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { ExpertBookingAvailability } from '../ExpertBookingAvailability/ExpertBookingAvailability';
import styles from './index.module.scss';

interface SectionProps {
  isHelpModal?: boolean;
  handleSingleExpertDetails?: (args: { show: boolean; expertId: number | null }) => void;
  showSingleExpertDetails?: { show: boolean; expertId: number | null };
}

export const ExpertComponent: FunctionComponent<SectionProps> = ({
  isHelpModal,
  handleSingleExpertDetails,
  showSingleExpertDetails,
}) => {
  const history = useHistory();
  const { expertId } = useParams<UrlParamsType>();
  const [expertDetails, setExpertDetails] = useState<ExpertInterface | null>(null);
  const [expertLoading, setExpertLoading] = useState<boolean>(true);
  const expertIdForData =
    isHelpModal &&
    showSingleExpertDetails &&
    showSingleExpertDetails?.show &&
    showSingleExpertDetails?.expertId
      ? showSingleExpertDetails?.expertId
      : expertId;

  useEffect(() => {
    if (expertIdForData) {
      getExpertDetails();
    }
  }, [expertIdForData]);

  const getExpertDetails = async () => {
    try {
      setExpertLoading(true);
      const response = await getExpertById(Number(expertIdForData));
      if (response?.success) {
        const data = castResponse<ExpertInterface>(response);

        setExpertDetails(data);
      } else {
        setExpertDetails(null);
      }
    } catch {
      setExpertDetails(null);
    } finally {
      setExpertLoading(false);
    }
  };

  const fullName = expertDetails
    ? `${expertDetails?.firstName || ''} ${expertDetails?.lastName || ''}`
    : '';

  const pageTitle = expertDetails ? `Expert: ${fullName}` : 'Expert Profile';

  return (
    <>
      {expertLoading && <LoadingBackdrop text="Personalised guidance is on the way…" />}

      <div className={styles.mainContainer}>
        <div className={styles.topBox} role="region" aria-labelledby="experts-introduction">
          <div className="container">
            <div className={styles.content}>
              <h1 className={styles.titleText} id="experts-introduction">
                Experts
              </h1>
              <MotionText
                as="p"
                text="We understand that taking charge of your wellbeing is a personal journey, and
                finding the right support makes all the difference. We have curated a diverse team
                of experts, each passionate about helping you thrive."
                className={styles.middleText}
              />

              <MotionText
                as="p"
                text=" Browse profiles of our experts, each showcasing their areas of specialisation and
                approach. Find someone who resonates with your needs and preferences. When
                you're ready to take the next step, simply book your appointment directly
                through the platform."
                className={styles.middleText}
              />
            </div>
          </div>
          <div className={styles.abstractImg} aria-hidden="true" />
        </div>

        <div className="container">
          <nav aria-label="Breadcrumb" className={styles.navigateBack}>
            <button
              onClick={() => {
                if (
                  isHelpModal &&
                  showSingleExpertDetails &&
                  showSingleExpertDetails?.show &&
                  handleSingleExpertDetails
                ) {
                  handleSingleExpertDetails({ show: false, expertId: null });
                } else {
                  history.push(`/experts`);
                }
              }}
              aria-label="Return to all experts page"
            >
              <Img src={SkyBluePrevious} alt="" aria-hidden="true" className={styles.previous} />
              <span>All experts</span>
            </button>
          </nav>

          <div className={styles.insideContainer}>
            <div>
              <article className={styles.expertProfileMain}>
                {expertLoading ? (
                  <div aria-live="polite" aria-busy="true">
                    <div className={styles.loadingItem}>
                      <Skeleton variant="text" width={200} height={60} />
                    </div>
                    <div className={styles.loadingItem}>
                      <Skeleton variant="circular" width={120} height={120} />
                    </div>
                    <div className={styles.loadingItem}>
                      <Skeleton variant="text" width={220} height={30} />
                    </div>
                    <span className="visually-hidden">Loading expert profile...</span>
                  </div>
                ) : (
                  <>
                    <h4 id="expert-name">{`${expertDetails?.firstName} ${expertDetails?.lastName}`}</h4>
                    <Img
                      src={`https://${expertDetails?.expertDetail?.profileImage}`}
                      alt={`Profile photo of ${fullName}`}
                      className={styles.expertProfileImg}
                    />
                    <p aria-labelledby="expert-experience">{`${expertDetails?.expertDetail.experience} years experience`}</p>
                  </>
                )}
              </article>

              <div className={styles.expertProfileSub}>
                {expertDetails?.expertDetail?.fullDescription &&
                  expertDetails?.expertDetail?.fullDescription?.length > 0 && (
                    <section className={styles.expertProfileAbout} aria-labelledby="about-me">
                      {expertLoading ? (
                        <Skeleton variant="text" width={100} height={60} />
                      ) : (
                        <h4 id="about-me">ABOUT ME</h4>
                      )}

                      {expertLoading ? (
                        <>
                          <Skeleton variant="text" width="100%" height={20} />
                          <Skeleton variant="text" width="100%" height={20} />
                          <Skeleton variant="text" width="100%" height={20} />
                          <Skeleton variant="text" width="100%" height={20} />
                        </>
                      ) : (
                        <p>
                          <p
                            dangerouslySetInnerHTML={{
                              __html: sanitizeHtml(
                                expertDetails.expertDetail.fullDescription,
                              ).replace(/\n/g, '<br/>'),
                            }}
                          />
                        </p>
                      )}

                      <p className={styles.qualificationContainer}>
                        <b id="expert-qualifications">Qualifications:</b>
                        <p
                          className={styles.qualification}
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(expertDetails.expertDetail.qualification).replace(
                              /\n/g,
                              '<br/>',
                            ),
                          }}
                          aria-labelledby="expert-qualifications"
                        />
                      </p>
                    </section>
                  )}

                {expertDetails?.expertsAllocatedTopics &&
                  expertDetails?.expertsAllocatedTopics?.length > 0 && (
                    <section
                      className={styles.expertProfileAreas}
                      aria-labelledby="areas-of-expertise"
                    >
                      {expertLoading ? (
                        <Skeleton variant="text" width={220} height={60} />
                      ) : (
                        <h4 id="areas-of-expertise">AREAS OF EXPERTISE</h4>
                      )}

                      <Grid container spacing={0}>
                        {expertLoading
                          ? [0, 1, 2, 3, 4, 5].map((index) => (
                              <Grid item xs={12} md={4} key={index}>
                                <Skeleton variant="text" width={120} height={30} />
                              </Grid>
                            ))
                          : expertDetails.expertsAllocatedTopics.map((topic, index) => (
                              <Grid item xs={12} md={4} key={index}>
                                <p>{topic.expertsTopic.topic}</p>
                              </Grid>
                            ))}
                      </Grid>
                    </section>
                  )}

                {expertDetails?.expertsAllocatedLanguages &&
                  expertDetails?.expertsAllocatedLanguages.length > 0 && (
                    <section
                      className={styles.expertProfileLanguages}
                      aria-labelledby="languages-spoken"
                    >
                      {expertLoading ? (
                        <Skeleton variant="text" width={220} height={60} />
                      ) : (
                        <h4 id="languages-spoken">LANGUAGES</h4>
                      )}

                      <Grid container spacing={0}>
                        {expertLoading
                          ? [0, 1, 2].map((index) => (
                              <Grid item xs={12} md={4} key={index}>
                                <Skeleton variant="text" width={120} height={30} />
                              </Grid>
                            ))
                          : expertDetails.expertsAllocatedLanguages.map((lng, index) => (
                              <Grid item xs={12} md={4} key={index}>
                                <p>{lng.expertsLanguage.language}</p>
                              </Grid>
                            ))}
                      </Grid>
                    </section>
                  )}
              </div>
            </div>

            {expertDetails && (
              <aside
                className={styles.expertProfileAvailability}
                aria-labelledby="booking-availability"
              >
                <h3 id="booking-availability" className="visually-hidden">
                  Booking Availability
                </h3>
                <ExpertBookingAvailability expertDetails={expertDetails} />
              </aside>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

type UrlParamsType = {
  expertId: string;
};

ExpertComponent.defaultProps = {
  isHelpModal: false,
  handleSingleExpertDetails: () => null,
  showSingleExpertDetails: { show: false, expertId: null },
};
